import React, { useState, useEffect, useRef } from 'react';
import { ActiveSelection, Canvas, FabricObject } from 'fabric';
import { getFabricTypeMeta } from '../../canvas/constants/object-meta';
import { LayerLabelResolver } from '../../canvas/editor-canvas-context';
import { isLayerObject } from '../../canvas/utils/canvas-pieces';

export type Layer = {
  object: FabricObject;
  stackIndex: number; // Matches the object's position in `canvas._objects` (0 = back)
  displayIndex: number; // Matches the layers panel order (0 = front)
  label: string;
};

export type UseLayerControlsReturn = {
  layers: Layer[];
  bringForward: (obj: FabricObject) => void;
  sendBackward: (obj: FabricObject) => void;
  moveNextTo: (obj: FabricObject, targetObj: FabricObject) => void;
  canBringForward: (obj: FabricObject) => boolean;
  canSendBackward: (obj: FabricObject) => boolean;
  focusLayer: (obj: FabricObject) => void;
  toggleLayerInSelection: (obj: FabricObject) => void;
};

const getLabelForObject = (obj: FabricObject, index: number): string =>
  `${getFabricTypeMeta(obj.type).label} ${index + 1}`;

const buildLayers = (canvas: Canvas, resolveLabel: LayerLabelResolver | null): Layer[] => {
  const objects = canvas.getObjects().filter(isLayerObject);
  const total = objects.length;

  return objects
    .map((obj, stackIndex) => {
      const defaultLabel = getLabelForObject(obj, stackIndex);
      return {
        object: obj,
        stackIndex,
        displayIndex: total - 1 - stackIndex,
        label: resolveLabel?.(obj) ?? defaultLabel
      };
    })
    .reverse(); // Reverse so front-most layers appear first in the panel
};

export const useLayerControls = (
  canvas: Canvas | null,
  resolveLabel: LayerLabelResolver | null
): UseLayerControlsReturn => {
  const [layers, setLayers] = useState<Layer[]>([]);

  const focusedRef = useRef<FabricObject | null>(null);
  const eventedSnapshotRef = useRef<Map<FabricObject, boolean>>(new Map());

  const syncLayers = React.useCallback(() => {
    if (!canvas) return;
    setLayers(buildLayers(canvas, resolveLabel));
  }, [canvas, resolveLabel]);

  // Only listen to object add/remove events since they are the only
  // external changes that affect layer order.
  // Layer controls update the state directly.
  useEffect(() => {
    if (!canvas) return;

    canvas.on('object:added', syncLayers);
    canvas.on('object:removed', syncLayers);

    // Initial sync
    syncLayers();

    return () => {
      canvas.off('object:added', syncLayers);
      canvas.off('object:removed', syncLayers);
    };
  }, [canvas, syncLayers]);

  const getLayerObjects = React.useCallback(
    (): FabricObject[] => canvas?.getObjects().filter(isLayerObject) ?? [],
    [canvas]
  );

  const getRawIndex = React.useCallback(
    (obj: FabricObject): number => canvas?.getObjects().indexOf(obj) ?? -1,
    [canvas]
  );

  const moveTo = React.useCallback(
    (obj: FabricObject, targetIndex: number) => {
      if (!canvas) return;
      canvas.moveObjectTo(obj, targetIndex);
      canvas.requestRenderAll();
      canvas.fire('object:modified', { target: obj });
      setLayers(buildLayers(canvas, resolveLabel));
    },
    [canvas, resolveLabel]
  );

  const moveAdjacent = React.useCallback(
    (obj: FabricObject, delta: 1 | -1) => {
      const items = getLayerObjects();
      const i = items.indexOf(obj);
      if (i < 0) return;
      const neighbor = items[i + delta];
      if (!neighbor) return;
      moveTo(obj, getRawIndex(neighbor));
    },
    [moveTo, canvas]
  );

  const bringForward = React.useCallback((obj: FabricObject) => moveAdjacent(obj, 1), [moveAdjacent]);

  const sendBackward = React.useCallback((obj: FabricObject) => moveAdjacent(obj, -1), [moveAdjacent]);

  // Places `obj` right where `targetObj` currently sits — the primitive `moveAdjacent` builds on
  // top of for the single-neighbor case. Exposed on its own so callers that need to reorder past
  // more than one object at a time (e.g. jumping a whole group of clips sharing a timeline lane)
  // can reuse the same Fabric move + `object:modified` notification without this hook knowing
  // anything about lanes/groups.
  const moveNextTo = React.useCallback(
    (obj: FabricObject, targetObj: FabricObject) => moveTo(obj, getRawIndex(targetObj)),
    [moveTo, getRawIndex]
  );

  const bringToFront = React.useCallback(
    (obj: FabricObject) => {
      const items = getLayerObjects();
      const last = items[items.length - 1];
      if (last && last !== obj) moveTo(obj, getRawIndex(last));
    },
    [moveTo, canvas]
  );

  const sendToBack = React.useCallback(
    (obj: FabricObject) => {
      const items = getLayerObjects();
      const first = items[0];
      if (first && first !== obj) moveTo(obj, getRawIndex(first));
    },

    [moveTo, canvas]
  );

  const moveToIndex = React.useCallback((obj: FabricObject, stackIndex: number) => moveTo(obj, stackIndex), [moveTo]);

  const canBringForward = React.useCallback(
    (obj: FabricObject): boolean => {
      const items = getLayerObjects();
      return items.indexOf(obj) < items.length - 1;
    },
    [canvas, layers]
  );

  const canSendBackward = React.useCallback(
    (obj: FabricObject): boolean => getLayerObjects().indexOf(obj) > 0,
    [canvas, layers]
  );

  const clearFocus = React.useCallback(() => {
    if (!focusedRef.current) return;
    eventedSnapshotRef.current.forEach((evented, obj) => {
      obj.evented = evented;
    });
    eventedSnapshotRef.current.clear();
    focusedRef.current = null;
  }, []);

  const focusLayer = React.useCallback(
    (obj: FabricObject) => {
      if (!canvas) return;
      clearFocus();

      canvas.getObjects().forEach(o => {
        if (!isLayerObject(o)) return;
        eventedSnapshotRef.current.set(o, o.evented !== false);
        o.evented = o === obj;
      });
      focusedRef.current = obj;

      canvas.setActiveObject(obj);
      canvas.fire('selection:created', { selected: [obj], selectedObjects: [obj] } as never);
      canvas.requestRenderAll();
    },
    [canvas, clearFocus]
  );

  // Exit focus when the user clicks empty canvas or the focused object is removed.
  useEffect(() => {
    if (!canvas) return;

    const handleCleared = () => clearFocus();
    const handleRemoved = (e: { target?: FabricObject }) => {
      if (e.target === focusedRef.current) clearFocus();
    };

    canvas.on('selection:cleared', handleCleared);
    canvas.on('object:removed', handleRemoved);

    return () => {
      canvas.off('selection:cleared', handleCleared);
      canvas.off('object:removed', handleRemoved);
    };
  }, [canvas, clearFocus]);

  // Adds/removes a layer from the current selection, supporting single and multi-selection.
  const toggleLayerInSelection = React.useCallback(
    (obj: FabricObject) => {
      if (!canvas) return;
      clearFocus();

      const active = canvas.getActiveObject();
      let next: FabricObject[];

      if (!active) {
        next = [obj];
      } else if (active instanceof ActiveSelection) {
        const current = active.getObjects();
        next = current.includes(obj) ? current.filter(o => o !== obj) : [...current, obj];
      } else {
        next = active === obj ? [] : [active, obj];
      }

      if (next.length === 0) {
        canvas.discardActiveObject();
        canvas.fire('selection:cleared', {} as never);
      } else if (next.length === 1) {
        canvas.setActiveObject(next[0]);
        canvas.fire('selection:created', { selected: next, selectedObjects: next } as never);
      } else {
        canvas.setActiveObject(new ActiveSelection(next, { canvas }));
        canvas.fire('selection:updated', { selected: next, selectedObjects: next } as never);
      }
      canvas.requestRenderAll();
    },
    [canvas, clearFocus]
  );

  return {
    layers,
    bringForward,
    sendBackward,
    moveNextTo,
    canBringForward,
    canSendBackward,
    focusLayer,
    toggleLayerInSelection
  };
};
